import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Archive,
  CalendarClock,
  CheckCircle2,
  CircleAlert,
  Edit3,
  ListTodo,
  Plus,
  ShieldCheck,
  X,
} from "lucide-react";
import type { Contact, Organization, Task } from "./types";
import type { WorkspaceRole } from "./RoleWorkspaces";
import { visibleTasks, taskSummary } from "./lib/taskModel";
import { getCurrentUserId, manageTask } from "./lib/api";
import { getTeamAccess, type TeamUser } from "./lib/teamAccess";

type Draft = {
  title: string;
  description: string;
  priority: NonNullable<Task["priority"]>;
  dueAt: string;
  contactId: string;
  organizationId: string;
  ownerUserId: string;
};
const blank = (): Draft => ({
  title: "",
  description: "",
  priority: "NORMAL",
  dueAt: "",
  contactId: "",
  organizationId: "",
  ownerUserId: "",
});
const localDate = (value?: string) =>
  value && value !== "No due date"
    ? new Date(
        new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 16)
    : "";

export function TaskWorkspace({
  tasks,
  setTasks,
  contacts,
  organizations,
  role,
  live,
  onOpenConversation,
  active=true,
}: {
  tasks: Task[];
  setTasks: (tasks: Task[]) => void;
  contacts: Contact[];
  organizations: Organization[];
  role: WorkspaceRole;
  live: boolean;
  onOpenConversation?: (task: Task) => void;
  active?: boolean;
}) {
  const [filter, setFilter] = useState<"open" | "complete" | "all">("open"),
    [query, setQuery] = useState(""),
    [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [open, setOpen] = useState(false),
    [editingRecord, setEditing] = useState<Task | null>(null),
    [draft, setDraft] = useState<Draft>(blank),
    [team, setTeam] = useState<TeamUser[]>([]),
    [currentUserId, setCurrentUserId] = useState<string | null>(null),
    [params] = useSearchParams(),
    navigate = useNavigate(),
    contextOpened = useRef('');
  const editing=editingRecord?tasks.find(task=>task.id===editingRecord.id)||editingRecord:null;
  const editingAvailable=!editingRecord||tasks.some(task=>task.id===editingRecord.id);
  useEffect(() => {
    if (role === "admin")
      getTeamAccess()
        .then((value) =>
          setTeam(value.users.filter((user) => user.status === "active")),
        )
        .catch(() => setTeam([]));
  }, [role]);
  useEffect(() => {
    let active = true;
    void getCurrentUserId().then((value) => {
      if (active) setCurrentUserId(value);
    }).catch(() => {
      if (active) setCurrentUserId(null);
    });
    return () => { active = false; };
  }, []);
  const [ownerFilter,setOwnerFilter]=useState<'mine'|'anyone'>('anyone');
  const scoped = useMemo(() => visibleTasks(tasks, role, currentUserId || undefined), [tasks, role, currentUserId]),
    summary = taskSummary(scoped),
    visible = scoped.filter(
      (task) =>
        (ownerFilter==='anyone'||task.ownerUserId===currentUserId) &&
        (filter === "all" ||
          (filter === "complete" ? task.completed : !task.completed)) &&
        (!query.trim() ||
          [
            task.title,
            task.description,
            task.contactName,
            task.organizationName,
            task.owner,
          ].some((value) =>
            value?.toLowerCase().includes(query.trim().toLowerCase()),
          )),
    );
  function begin(task?: Task) {
    if(task?.prospectSequenceId){navigate('/work/queue?prospectTask='+encodeURIComponent(task.id));return}
    setEditing(task || null);
    setDraft(
      task
        ? {
            title: task.title,
            description: task.description || "",
            priority: task.priority || "NORMAL",
            dueAt: localDate(task.dueAt),
            contactId: task.contactId || "",
            organizationId: task.organizationId || "",
            ownerUserId: task.ownerUserId || "",
          }
        : blank(),
    );
    setError("");
    setOpen(true);
  }
  useEffect(() => {
    if(!active)return;
    const taskId=params.get('task');
    const contactId = params.get("contact") || "",
      organizationId = params.get("organization") || "",
      title = params.get("title") || "";
    if (!taskId&&!contactId&&!organizationId)return;
    const request=JSON.stringify([taskId,contactId,organizationId,title]);
    if(contextOpened.current===request)return;
    const task=taskId?scoped.find(item=>item.id===taskId):undefined;
    if(taskId&&!task)return;
    contextOpened.current=request;
    if(open&&!window.confirm('Open another follow-up and discard the current unsaved task draft?'))return;
    if(task){begin(task);return}
    setEditing(null);
    setDraft({
      ...blank(),
      contactId,
      organizationId,
      title: title || "Customer follow-up",
    });
    setError("");
    setOpen(true);
    navigate("/tasks", { replace: true });
  // Explicit links are handled once; hidden Work views cannot replace an open editor.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate, scoped, active]);
  async function persist(
    action: "CREATE" | "UPDATE" | "COMPLETE" | "REOPEN" | "CANCEL" | "ARCHIVE",
    task: Task | null | undefined = editing,
  ) {
    if(task&&!tasks.some(item=>item.id===task.id)){setError('This task is absent from the bounded result. Deletion or access status is unverified; refresh before saving.');return}
    const id = task?.id || crypto.randomUUID(),
      useDraft = action === "CREATE" || action === "UPDATE",
      source = useDraft
        ? {
            title: draft.title,
            description: draft.description,
            priority: draft.priority,
            ownerUserId: draft.ownerUserId || (await getCurrentUserId()),
            dueAt: draft.dueAt ? new Date(draft.dueAt).toISOString() : null,
            organizationId: draft.organizationId || null,
            contactId: draft.contactId || null,
            opportunityId: task?.opportunityId || null,
            scheduleEventId: task?.scheduleEventId || null,
          }
        : {
            title: task?.title || "",
            description: task?.description || "",
            priority: task?.priority || "NORMAL",
            ownerUserId: task?.ownerUserId || null,
            dueAt: task?.dueAt === "No due date" ? null : task?.dueAt || null,
            organizationId: task?.organizationId || null,
            contactId: task?.contactId || null,
            opportunityId: task?.opportunityId || null,
            scheduleEventId: task?.scheduleEventId || null,
          };
    setBusy(id);
    setError("");
    try {
      const saved = await manageTask({ action, taskId: id, ...source });
      setTasks(
        action === "ARCHIVE"
          ? tasks.filter((item) => item.id !== id)
          : [saved, ...tasks.filter((item) => item.id !== id)],
      );
      if (useDraft) {
        setOpen(false);
        setEditing(null);
        setDraft(blank());
      }
    } catch (value) {
      setError(value instanceof Error ? value.message : "task_update_failed");
    } finally {
      setBusy("");
    }
  }
  return (
    <div className="page task-workspace-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">ACCOUNTABLE FOLLOW-UP</span>
          <h1>Tasks with an owner, due point, and customer context</h1>
          <p>
            Loaded CRM task records only. Backend authorization remains
            authoritative.
          </p>
        </div>
        <div className="task-role-boundary">
          <ShieldCheck size={15} />
          {role === "admin"
            ? "Company-wide administrative view"
            : `${role.toUpperCase()} assigned view`}
          <button className="primary-btn" onClick={() => begin()}>
            <Plus size={14} />
            Add task
          </button>
        </div>
      </div>
      <section className="task-summary-grid">
        <div>
          <span>Open</span>
          <strong>{summary.open}</strong>
        </div>
        <div>
          <span>Complete</span>
          <strong>{summary.complete}</strong>
        </div>
        <div>
          <span>Visible total</span>
          <strong>{summary.total}</strong>
        </div>
        <div>
          <span>Missing owner</span>
          <strong>{summary.unowned}</strong>
        </div>
      </section>
      <div className="task-filter-row">
        <label>Assignment<select aria-label="Follow-up assignment" value={ownerFilter} onChange={event=>setOwnerFilter(event.target.value as 'mine'|'anyone')}><option value="mine">My work</option><option value="anyone">Anyone</option></select></label>
        <input
          aria-label="Search tasks"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search tasks"
        />
        {(["open", "complete", "all"] as const).map((value) => (
          <button
            key={value}
            className={filter === value ? "active" : ""}
            onClick={() => setFilter(value)}
          >
            {value}
          </button>
        ))}
        <button
          onClick={() => {
            setQuery("");
            setFilter("open");
          }}
        >
          Clear filters
        </button>
      </div>
      {error && (
        <div className="composer-error" role="alert">
          {error.replaceAll("_", " ")}
        </div>
      )}
      <section className="panel task-record-list">
        {visible.map((task) => (
          <article key={task.id} className={task.completed ? "complete" : ""}>
            <div className="task-record-icon">
              {task.completed ? (
                <CheckCircle2 size={17} />
              ) : (
                <ListTodo size={17} />
              )}
            </div>
            <div>
              <strong>{task.title}</strong>
              <span>
                {task.contactName || "No contact attached"} ·{" "}
                {task.organizationName || "No organization attached"}
              </span>
              <small>
                <CalendarClock size={12} />
                {task.dueAt} · {task.priority || "NORMAL"} · Owner:{" "}
                {task.owner || "Unassigned"}
              </small>
              {task.description && <p>{task.description}</p>}
            </div>
            {task.prospectSequenceId?<button type="button" onClick={()=>navigate('/work/queue?prospectTask='+encodeURIComponent(task.id))}>Open governed prospecting work</button>:live ? (
              <div className="team-actions">
                {onOpenConversation&&task.contactId&&contacts.some(contact=>contact.id===task.contactId)&&<button type="button" aria-label={'Open conversation for '+task.title} onClick={()=>onOpenConversation(task)}>Open conversation</button>}
                <button
                  disabled={busy === task.id}
                  onClick={() =>
                    void persist(task.completed ? "REOPEN" : "COMPLETE", task)
                  }
                >
                  {busy === task.id
                    ? "Saving…"
                    : task.completed
                      ? "Reopen"
                      : "Complete"}
                </button>
                <button onClick={() => begin(task)}>
                  <Edit3 size={13} />
                  Edit
                </button>
                <button
                  disabled={busy === task.id || task.status === "CANCELLED"}
                  onClick={() => void persist("CANCEL", task)}
                >
                  Cancel
                </button>
                <button
                  disabled={busy === task.id}
                  onClick={() => void persist("ARCHIVE", task)}
                >
                  <Archive size={13} />
                  Archive
                </button>
              </div>
            ) : (
              <b>{task.completed ? "Complete" : "Open"}</b>
            )}
          </article>
        ))}
        {!visible.length && (
          <div className="task-empty">
            <CircleAlert size={24} />
            <h2>No {filter} tasks</h2>
            <p>
              No record is inferred. Adjust the filter or verify the configured
              task adapter.
            </p>
          </div>
        )}
      </section>
      {open && (
        <div className="record-modal-backdrop">
          <form
            className="record-modal"
            onSubmit={async (event) => {
              event.preventDefault();
              if (
                !draft.title.trim() ||
                (!draft.contactId && !draft.organizationId)
              ) {
                setError(
                  "Choose a contact or company for accountable context.",
                );
                return;
              }
              if(!editingAvailable){setError("Selected task is absent from this bounded result; access or deletion is unverified.");return}
              await persist(editing ? "UPDATE" : "CREATE");
            }}
          >
            <header>
              <h2>{editing ? "Edit task" : "Create task"}</h2>
              {!editingAvailable&&<p role="status">Selected task is absent from the bounded result. Deletion or access is unverified; the unsaved draft is retained.</p>}
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <X />
              </button>
            </header>
            <label>
              Title
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                required
              />
            </label>
            <label>
              Contact
              <select
                value={draft.contactId}
                onChange={(e) =>
                  setDraft({ ...draft, contactId: e.target.value })
                }
              >
                <option value="">No contact</option>
                {contacts.map((x) => (
                  <option value={x.id} key={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Company
              <select
                value={draft.organizationId}
                onChange={(e) =>
                  setDraft({ ...draft, organizationId: e.target.value })
                }
              >
                <option value="">No company</option>
                {organizations.map((x) => (
                  <option value={x.id} key={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Owner
              <select
                value={draft.ownerUserId}
                onChange={(e) =>
                  setDraft({ ...draft, ownerUserId: e.target.value })
                }
              >
                <option value="">Assign to me</option>
                {team.map((user) => (
                  <option value={user.id} key={user.id}>
                    {user.firstName} {user.lastName} · {user.role}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Description
              <textarea
                value={draft.description}
                onChange={(e) =>
                  setDraft({ ...draft, description: e.target.value })
                }
              />
            </label>
            <label>
              Priority
              <select
                value={draft.priority}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    priority: e.target.value as Draft["priority"],
                  })
                }
              >
                {["LOW", "NORMAL", "HIGH", "URGENT"].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label>
              Due date
              <input
                type="datetime-local"
                value={draft.dueAt}
                onChange={(e) => setDraft({ ...draft, dueAt: e.target.value })}
              />
            </label>
            {error && (
              <div className="composer-error" role="alert">
                {error.replaceAll("_", " ")}
              </div>
            )}
            <footer>
              <button type="button" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button className="primary-btn" disabled={Boolean(busy)}>
                {busy ? "Saving…" : editing ? "Save task" : "Create task"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
